import className from 'classnames/bind';
import { motion, useViewportScroll, useTransform, transform } from 'framer-motion';
import React from 'react';
import { useInView } from 'react-intersection-observer';

import Heading from 'components/shared/heading';
import { BREAKPOINT_SM } from 'constants/breakpoints';
import useLottie from 'hooks/use-lottie';
import useSectionOffset from 'hooks/use-section-offset';
import useWindowWidth from 'hooks/use-window-width';
import Arrow from 'images/pages/home/dropping/arrow.inline.svg';
import Dialog from 'images/pages/home/dropping/dialog.inline.svg';
import Lightning from 'images/pages/home/dropping/lightning.inline.svg';
import Lotus from 'images/pages/home/dropping/lotus.inline.svg';

import animationData from './animation/tape';
import styles from './dropping.module.scss';
import SneakersBackground from './sneakers-background';

const cx = className.bind(styles);

const VARIANTS_INTERVALS = [0, 0.8, 0.99, 1];
const WORDS_VERTICAL_OFFSET_REM = 1.3;
const WORDS_VERTICAL_OFFSET_SM_REM = 2.5;
const VARIANTS_TEXT_Y = {
  lg: [
    ['0rem', '0rem', '0rem', '0rem'],
    ['0rem', '0rem', `${WORDS_VERTICAL_OFFSET_REM * 1}rem`, `${WORDS_VERTICAL_OFFSET_REM * 1}rem`],
    ['0rem', '0rem', `${WORDS_VERTICAL_OFFSET_REM * 2}rem`, `${WORDS_VERTICAL_OFFSET_REM * 2}rem`],
    ['0rem', '0rem', `${WORDS_VERTICAL_OFFSET_REM * 3}rem`, `${WORDS_VERTICAL_OFFSET_REM * 3}rem`],
    ['0rem', '0rem', `${WORDS_VERTICAL_OFFSET_REM * 4}rem`, `${WORDS_VERTICAL_OFFSET_REM * 4}rem`],
  ],
  sm: [
    ['0rem', '0rem', '0rem', '0rem'],
    [
      '0rem',
      '0rem',
      `${WORDS_VERTICAL_OFFSET_SM_REM * 1}rem`,
      `${WORDS_VERTICAL_OFFSET_SM_REM * 1}rem`,
    ],
    [
      '0rem',
      '0rem',
      `${WORDS_VERTICAL_OFFSET_SM_REM * 2}rem`,
      `${WORDS_VERTICAL_OFFSET_SM_REM * 2}rem`,
    ],
    [
      '0rem',
      '0rem',
      `${WORDS_VERTICAL_OFFSET_SM_REM * 3}rem`,
      `${WORDS_VERTICAL_OFFSET_SM_REM * 3}rem`,
    ],
    [
      '0rem',
      '0rem',
      `${WORDS_VERTICAL_OFFSET_SM_REM * 4}rem`,
      `${WORDS_VERTICAL_OFFSET_SM_REM * 4}rem`,
    ],
  ],
};

const BULLETS = [
  {
    icon: <Lightning />,
    lines: [
      'Nvidia deliberately refuses',
      'to announce release times;',
      'Sony shock drops',
      'preorders.',
    ],
  },
  {
    icon: <Dialog />,
    lines: [
      'What if you got',
      'a notification telling you',
      'when. What if you could',
      'just sit back and let us',
      'handle it after some prep.',
    ],
  },
  {
    icon: <Lotus />,
    lines: ["Don't stress about", 'being ready,', 'just be relaxed ready', 'and waiting.'],
  },
];

let hasPlayedTapeAnimation = false;

const Dropping = () => {
  const outerSectionRef = React.useRef(null);
  const { scrollY } = useViewportScroll();
  const [scrollTape, setScrollTape] = React.useState(null);

  const [sectionStart, sectionEnd, sectionHidden] = useSectionOffset(outerSectionRef);

  const windowWidth = useWindowWidth();

  React.useLayoutEffect(() => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const screenHeight = document.documentElement.clientHeight;

    const tapeElement = document.getElementById('tape');
    const tapeRect = tapeElement.getBoundingClientRect();

    // tape end is at the bottom of the screen
    const offsetTapeEnd = tapeRect.top + scrollTop + tapeRect.height - screenHeight;

    setScrollTape(offsetTapeEnd);
  }, [windowWidth]);

  // Tape animation
  const defaultOptions = {
    loop: false,
    autoplay: false,
    renderer: 'svg',
    animationData,
  };

  const [lottieLoaded, setLottieLoaded] = React.useState(false);
  const [sectionRef, inView] = useInView({});

  const [lottieAnimation, lottieAnimationRef] = useLottie(defaultOptions, {
    loaded_images() {
      setLottieLoaded(true);
    },
    complete() {
      this.loop = false;
      this.pause();
    },
  });

  const inputRange = VARIANTS_INTERVALS.map(
    (input) => sectionStart + (sectionEnd - sectionStart) * input
  );

  const getLineStyles = (lineNumber) => {
    const size = windowWidth > BREAKPOINT_SM ? 'lg' : 'sm';
    /* eslint-disable react-hooks/rules-of-hooks */
    const linePositionY = useTransform(scrollY, inputRange, VARIANTS_TEXT_Y[size][lineNumber]);
    return { y: linePositionY };
  };

  const [droppingMarginBottom, setDroppingMarginBottom] = React.useState(0);

  React.useEffect(() => {
    const droppingElementMargins = windowWidth > BREAKPOINT_SM ? [0, 5] : [0, 10];

    const scrollHandler = (evt) => {
      const droppingStartOffset = sectionStart + (sectionEnd - sectionStart) * 0.55;
      const droppingEndOffset = sectionStart + (sectionEnd - sectionStart) * 0.68;

      // change 'dropping' positions
      if (window.scrollY >= droppingStartOffset && window.scrollY <= droppingEndOffset) {
        const newMargin = transform(
          window.scrollY,
          [droppingStartOffset, droppingEndOffset],
          droppingElementMargins
        );
        setDroppingMarginBottom(newMargin);
      }

      if (window.scrollY < droppingStartOffset) {
        // hide all 'dropping' copies
        setDroppingMarginBottom(droppingElementMargins[0]);
      }

      if (window.scrollY > droppingEndOffset) {
        // show all 'dropping' copies
        setDroppingMarginBottom(droppingElementMargins[1]);
      }

      if (
        window.scrollY >= scrollTape &&
        window.scrollY < sectionHidden &&
        !hasPlayedTapeAnimation
      ) {
        lottieAnimation.play();
        hasPlayedTapeAnimation = false;
      }
    };

    if (sectionEnd && sectionStart) {
      document.addEventListener('scroll', scrollHandler);
    }

    return () => document.removeEventListener('scroll', scrollHandler);
  }, [sectionStart, sectionEnd, sectionHidden, scrollTape, lottieAnimation, windowWidth]);

  const Title = () => (
    <Heading className={cx('title')} tag="h2" size="xl" theme="secondary-inverted">
      <span className={cx('title-accented')}>What</span>&nbsp;is
      <span
        className={cx('dropping-container')}
        id="dropping-container"
        style={{ marginBottom: `${droppingMarginBottom}rem` }}
      >
        <span className={cx('dropping-top')}>dropping</span>
        {[1, 2, 3, 4].map((num, index) => (
          <motion.span
            key={`dropping-row-${index}`}
            style={getLineStyles(num)}
            className={cx('dropping-item', `dropping-item-${index}`)}
          >
            dropping
          </motion.span>
        ))}
      </span>
      and&nbsp;
      <span className={cx('title-accented')}>when</span>
    </Heading>
  );

  const TitleSmall = () => (
    <Heading className={cx('title')} tag="h2" size="xl" theme="secondary-inverted">
      <span className={cx('title-accented')}>What</span>
      <span className={cx('title-accented')}>is</span>
      <span
        className={cx('dropping-container')}
        id="dropping-top"
        style={{ marginBottom: `${droppingMarginBottom}rem` }}
      >
        <span className={cx('dropping-top')}>drop&#8209;</span>
        {[1, 2, 3, 4].map((num, index) => (
          <motion.span
            key={`dropping-row-${index}`}
            style={getLineStyles(num)}
            className={cx('dropping-item', `dropping-item-${index}`)}
          >
            drop&#8209;
          </motion.span>
        ))}
      </span>
      <span
        className={cx('dropping-container')}
        id="dropping-bottom"
        style={{ marginBottom: `${droppingMarginBottom}rem` }}
      >
        <span className={cx('dropping-top')}>ping</span>
        {[1, 2, 3, 4].map((num, index) => (
          <motion.span
            key={`dropping-row-${index}`}
            style={getLineStyles(num)}
            className={cx('dropping-item', `dropping-item-${index}`)}
          >
            ping
          </motion.span>
        ))}
      </span>
      <span>and</span>
      <span className={cx('title-accented')}>when</span>
    </Heading>
  );

  return (
    <section ref={outerSectionRef}>
      <div className={cx('container', 'row', 'wrapper')} ref={sectionRef}>
        {windowWidth > BREAKPOINT_SM ? <Title /> : <TitleSmall />}
        <div className={cx('tape-wrapper')} id="tape">
          <div className={cx('tape')}>
            <div className={cx('lottie')} ref={lottieAnimationRef} />
            <div className={cx('bullets')}>
              {BULLETS.map(({ icon, lines }, i) => (
                <div className={cx('bullet')} key={`bullet-${i}`}>
                  <div className={cx('bullet-icon')}>{icon}</div>
                  <div className={cx('bullet-text')}>
                    {lines.map((l, ind) => (
                      <span key={`bullet-${i}-${ind}`}>{l}</span>
                    ))}
                  </div>
                </div>
              ))}
              <div className={cx('arrow', 'arrow-left')}>
                <Arrow />
              </div>
              <div className={cx('arrow', 'arrow-right')}>
                <Arrow />
              </div>
            </div>
          </div>
        </div>

        <SneakersBackground isAnimated={inView} />
      </div>
    </section>
  );
};

export default Dropping;
